/**
 * API client.
 *
 * The session is an HttpOnly cookie, so there is no token to hold in memory and
 * nothing for a script on the page to steal. Every call therefore sends
 * credentials and treats a 401 as "signed out".
 */

export interface ApiErrorBody {
  error: { code: string; message: string; [key: string]: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody | undefined, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'unknown';
    this.details = (body?.error ?? {}) as Record<string, unknown>;
  }
}

export interface User {
  id: number;
  email: string;
  display_name: string;
  role: 'viewer' | 'editor' | 'admin';
  timezone: string;
}

export interface Stream {
  id: number;
  name: string;
  slug: string;
  codec: string | null;
  bitrate_kbps: number | null;
  retention_hours: number;
  is_live: boolean;
  last_seen_at: string | null;
  /** The source, for listening to the broadcast as it happens (see D9). */
  live_url: string | null;
  coverage: { from: string | null; to: string | null };
  archive_bytes: number;
  segment_count: number;
}

export interface BlockSegment {
  id: number;
  started_at: string;
  ended_at: string | null;
  duration_sec: number | null;
  covers_from: string;
  covers_to: string | null;
  trim_start_sec: number;
  size_bytes: number | null;
  peaks_url: string | null;
  /** Interleaved min/max pairs for the tile; full resolution is at peaks_url. */
  thumbnail: number[] | null;
  /** Detected pauses, or null until they have been found. */
  gaps_url: string | null;
  is_recording: boolean;
}

export type BlockStatus = 'ready' | 'recording' | 'partial' | 'missing' | 'expired';

export interface Block {
  block_start_at: string;
  block_end_at: string;
  status: BlockStatus;
  expires_at: string;
  has_gap: boolean;
  covered_sec: number;
  segments: BlockSegment[];
}

export type ExportStatus = 'queued' | 'processing' | 'ready' | 'failed' | 'purged';

export interface ExportRecord {
  id: number;
  stream_id: number;
  label: string | null;
  range_start_at: string;
  range_end_at: string;
  duration_sec: number;
  output_format: string;
  status: ExportStatus;
  error_message: string | null;
  file_size_bytes: number | null;
  expires_at: string;
  created_at: string;
  completed_at: string | null;
  download_url: string | null;
  download_count: number;
  last_downloaded_at: string | null;
}

export interface AdminUser {
  id: number;
  email: string;
  display_name: string;
  role: 'viewer' | 'editor' | 'admin';
  timezone: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface AuditEntry {
  id: number;
  action: string;
  target_type: string | null;
  target_id: number | null;
  ip: string | null;
  created_at: string;
  actor: string | null;
}

export interface AdminHealth {
  disk: { free_bytes: number; total_bytes: number; free_percent: number; floor_percent: number };
  streams: Array<{
    id: number; slug: string; name: string;
    recorder_restarts: number; recorder_started_at: string | null;
    last_seen_age_seconds: number | null; archive_bytes: number;
    segments: number; purge_lag_seconds: number;
  }>;
  jobs: Array<{ kind: string; status: string; count: number }>;
  config: { segment_seconds: number; default_retention_hours: number; export_retention_hours: number };
}

export interface TranscriptLine {
  fromMs: number;
  toMs: number;
  text: string;
}

export type TranscriptSource = 'range' | 'export' | 'upload';

export interface Transcript {
  id: number;
  status: 'queued' | 'processing' | 'ready' | 'failed' | 'cancelled';
  source: TranscriptSource;
  /** The export's label, or the uploaded filename. Null for a picked range. */
  source_name: string | null;
  export_id: number | null;
  /** Whether the audio behind it still exists. False means it cannot render. */
  audio_available: boolean;
  stream_id: number | null;
  range_start_at: string | null;
  range_end_at: string | null;
  created_at: string;
  language: string | null;
  model: string | null;
  lines: TranscriptLine[];
  text: string | null;
  error_message: string | null;
  duration_sec: number | null;
  took_ms: number | null;
  /**
   * How long the server expects this to take in total, in seconds, from its own
   * measured speed. Null once it is no longer waiting on anything. The page
   * paces its wait against this rather than against a rate compiled into it,
   * because the machine that decodes is not the machine this was written on.
   */
  expected_sec: number | null;
}


export interface Cue {
  fromMs: number;
  toMs: number;
  text: string;
}

export interface CueProblem {
  index: number;
  kind: 'too_long' | 'too_short' | 'overlaps' | 'empty';
}

export interface CueSet {
  cues: Cue[];
  /** False until a person has saved them; a video needs a reviewed set. */
  edited: boolean;
  edited_at: string | null;
  duration_sec: number | null;
  problems: CueProblem[];
  /** What to put in the wording fields to begin with. Suggestions, not magic. */
  suggested_texts: { station: string; stamp: string };
}

export type VideoAspect = 'vertical' | 'square';

/**
 * How large the card is drawn. `small` is the whole card at 70% -- width,
 * height, chrome and type together -- not smaller words in the same box.
 */
export type VideoCardSize = 'default' | 'small';

/** Everything written on the card except the subtitle. All editable. */
export interface CardTexts {
  /** The guest's name, shown above the headline. */
  guestName: string;
  title: string;
  station: string;
  stamp: string;
  footer: string;
}

export interface VideoRecord {
  id: number;
  transcript_id: number;
  aspect: VideoAspect;
  title: string | null;
  status: 'queued' | 'rendering' | 'ready' | 'failed' | 'cancelled' | 'purged';
  /**
   * Renders in front of this one. 0 means it is the one being worked on, null
   * once there is nothing left to wait for. One worker runs one job at a time,
   * so this is how a second person is told they are waiting rather than stuck.
   */
  queued_ahead: number | null;
  error_message: string | null;
  file_size_bytes: number | null;
  duration_sec: number | null;
  cue_count: number;
  expires_at: string;
  created_at: string;
  completed_at: string | null;
  download_url: string | null;
  /** Same file as the download, for playing in the page before posting it. */
  stream_url: string | null;
  texts: CardTexts;
  has_image: boolean;
  has_background: boolean;
  has_background_video: boolean;
  animated: boolean;
  card_size: VideoCardSize;
  /** 0 to 100 while rendering. */
  progress: number;
  width: number;
  height: number;
}

const BASE = '/api/v1';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Built rather than spread with an optional `headers`, because under
  // exactOptionalPropertyTypes an explicit `undefined` is not the same as an
  // absent property.
  const options: RequestInit = { credentials: 'same-origin', ...init };
  if (init?.body) options.headers = { 'Content-Type': 'application/json' };

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, options);
  } catch {
    // A network failure is not an API error and must not read like one.
    throw new ApiError(0, undefined, 'network');
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody, 'request failed');
  }
  return body as T;
}

export const api = {
  me: () => request<User>('/auth/me'),

  login: (email: string, password: string) =>
    request<{ id: number; email: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  streams: () => request<Stream[]>('/streams'),

  blocks: (streamId: number) => request<Block[]>(`/streams/${streamId}/blocks`),

  adminHealth: () => request<AdminHealth>('/admin/health'),
  adminUsers: () => request<AdminUser[]>('/admin/users'),
  adminAudit: (limit = 60) => request<AuditEntry[]>(`/admin/audit?limit=${limit}`),

  createUser: (body: {
    email: string; display_name: string; password: string; role: string;
  }) => request<AdminUser>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),

  updateUser: (id: number, body: Record<string, unknown>) =>
    request<AdminUser>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  updateStream: (id: number, body: Record<string, unknown>) =>
    request<Stream>(`/streams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** Audio is fetched by the <audio> element itself, which sends the cookie. */
  audioUrl: (segmentId: number) => `${BASE}/segments/${segmentId}/audio`,

  transcribe: (body: { stream_id: number; range_start_at: string; range_end_at: string }) =>
    request<Transcript>('/transcripts', { method: 'POST', body: JSON.stringify(body) }),

  transcript: (id: number) => request<Transcript>(`/transcripts/${id}`),


  cues: (transcriptId: number) => request<CueSet>(`/transcripts/${transcriptId}/cues`),

  saveCues: (transcriptId: number, cues: Cue[]) =>
    request<CueSet>(`/transcripts/${transcriptId}/cues`, {
      method: 'PUT',
      body: JSON.stringify({ cues }),
    }),

  videos: () => request<{ videos: VideoRecord[] }>('/videos').then((r) => r.videos),

  video: (id: number) => request<VideoRecord>(`/videos/${id}`),
  cancelVideo: (id: number) =>
    request<VideoRecord>(`/videos/${id}/cancel`, { method: 'POST' }),
  cancelTranscript: (id: number) =>
    request<Transcript>(`/transcripts/${id}/cancel`, { method: 'POST' }),

  createVideo: (body: {
    transcript_id: number;
    aspect: VideoAspect;
    title: string | null;
    texts: CardTexts;
    image_key: string | null;
    background_key: string | null;
    background_video_key: string | null;
    animated: boolean;
    card_size: VideoCardSize;
  }) => request<VideoRecord>('/videos', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * The browser sets the multipart boundary itself, so this goes around
   * `request` rather than through it.
   */
  /** Footage for the background. Same shape as the picture upload, other route. */
  uploadBackgroundVideo: async (file: File): Promise<{ background_video_key: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${BASE}/videos/background-video`, {
      method: 'POST', body: form, credentials: 'same-origin',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as ApiErrorBody | undefined;
      throw new ApiError(response.status, body, 'Upload failed');
    }
    return await response.json() as { background_video_key: string };
  },

  uploadVideoImage: async (file: File): Promise<{ image_key: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${BASE}/videos/image`, {
      method: 'POST', body: form, credentials: 'same-origin',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as ApiErrorBody | undefined;
      throw new ApiError(response.status, body, 'Upload failed');
    }
    return await response.json() as { image_key: string };
  },

  transcripts: () =>
    request<{ transcripts: Transcript[] }>('/transcripts').then((r) => r.transcripts),

  transcribeExport: (exportId: number) =>
    request<Transcript>(`/transcripts/from-export/${exportId}`, { method: 'POST' }),

  /**
   * Uploads audio for transcription, reporting how much of the file has gone.
   *
   * XMLHttpRequest rather than fetch, and not by preference: fetch has no way
   * to observe an upload's progress, and on a control-room connection a
   * producer sending a long recording deserves to see it moving rather than
   * guess whether it has stalled.
   *
   * The browser sets the multipart boundary itself, which is why FormData goes
   * in untouched -- adding an explicit content-type header produces a body the
   * server cannot parse.
   */
  transcribeUpload: (
    file: File,
    onProgress?: (fraction: number) => void,
  ): Promise<Transcript> => new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/transcripts/upload`);
    xhr.withCredentials = true;

    xhr.upload.addEventListener('progress', (event) => {
      // `lengthComputable` is false when the browser cannot know the total,
      // in which case there is no honest fraction to report.
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(event.loaded / event.total);
      }
    });

    xhr.addEventListener('load', () => {
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        reject(new ApiError(xhr.status, undefined, 'Upload failed'));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve(body as Transcript);
      } else {
        reject(new ApiError(xhr.status, body as ApiErrorBody, 'Upload failed'));
      }
    });

    xhr.addEventListener('error', () =>
      reject(new ApiError(0, undefined, 'Upload failed')));
    xhr.addEventListener('abort', () =>
      reject(new ApiError(0, undefined, 'Upload cancelled')));

    xhr.send(form);
  }),

  exports: () => request<ExportRecord[]>('/exports'),

  export: (id: number) => request<ExportRecord>(`/exports/${id}`),

  createExport: (body: {
    stream_id: number;
    range_start_at: string;
    range_end_at: string;
    output_format: string;
    label: string | null;
  }) => request<ExportRecord>('/exports', { method: 'POST', body: JSON.stringify(body) }),
};
